import { useState } from 'react';

export default function AboutPage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white font-playfair">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            {/* Left Navigation - Desktop */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="/for-attorneys" className="text-gray-900 hover:text-teal-600 font-medium tracking-wide transition-colors">For Attorneys</a>
              <a href="/#services" className="text-gray-900 hover:text-teal-600 font-medium tracking-wide transition-colors">Services</a>
            </div>

            {/* Centered Logo */}
            <div className="flex items-center justify-center flex-grow md:flex-grow-0">
              <h1>
                <a href="/">
                  <img 
                    src="https://static.readdy.ai/image/a19705f045df358eda503236ce656b36/846ee1e93f81f94b53dc3e9fa39ac791.png" 
                    alt="VTC Legal - Fractional General Counsel for AI and Tech Companies" 
                    className="h-20 w-auto"
                  />
                </a>
              </h1>
            </div>
            
            {/* Right Navigation - Desktop */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="/about" className="text-teal-600 font-medium tracking-wide transition-colors">About</a>
              <a href="/#how-it-works" className="text-gray-900 hover:text-teal-600 font-medium tracking-wide transition-colors">How It Works</a>
              <a href="/#contact" className="bg-black text-white px-8 py-3 rounded-md hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer font-medium">Get Started</a>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden absolute right-4">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-900 hover:text-teal-600 cursor-pointer"
              >
                <i className="ri-menu-line text-xl"></i>
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <div className="flex flex-col space-y-4">
                <a href="/for-attorneys" className="text-gray-900 hover:text-teal-600 font-medium">For Attorneys</a>
                <a href="/#services" className="text-gray-900 hover:text-teal-600 font-medium">Services</a>
                <a href="/about" className="text-teal-600 font-medium">About</a>
                <a href="/#how-it-works" className="text-gray-900 hover:text-teal-600 font-medium">How It Works</a>
                <a href="/#contact" className="bg-black text-white px-8 py-3 rounded-md hover:bg-gray-800 transition-colors text-center cursor-pointer font-medium">Get Started</a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-8 leading-tight font-playfair">
              About VTC Legal
            </h2>
            <p className="text-xl text-gray-700 mb-12 max-w-3xl mx-auto leading-relaxed font-inter">
              Business-focused legal counsel for technology companies navigating complex regulatory landscapes
            </p>
          </div>
        </div>
      </section>

      {/* Business-Driven Approach */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 font-playfair">Business-Driven Legal Strategy</h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto font-inter">
              We don't just provide legal advice—we deliver strategic business solutions that drive growth and maximize value
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-12">
            <div className="bg-white p-10 rounded-2xl shadow-sm border border-gray-200">
              <div className="w-16 h-16 bg-teal-600 rounded-2xl flex items-center justify-center mb-8">
                <i className="ri-line-chart-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 font-playfair">ROI-Focused Decisions</h3>
              <p className="text-gray-700 leading-relaxed font-inter">
                Every legal recommendation includes business impact analysis. We measure success by your company's growth, not just legal compliance.
              </p>
            </div>

            <div className="bg-white p-10 rounded-2xl shadow-sm border border-gray-200">
              <div className="w-16 h-16 bg-teal-600 rounded-2xl flex items-center justify-center mb-8">
                <i className="ri-rocket-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 font-playfair">Growth-Enabling Solutions</h3>
              <p className="text-gray-700 leading-relaxed font-inter">
                Strategic legal frameworks designed to accelerate expansion, not slow it down. We understand that speed-to-market drives competitive advantage.
              </p>
            </div>

            <div className="bg-white p-10 rounded-2xl shadow-sm border border-gray-200">
              <div className="w-16 h-16 bg-teal-600 rounded-2xl flex items-center justify-center mb-8">
                <i className="ri-funds-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 font-playfair">Cost-Efficient Excellence</h3>
              <p className="text-gray-700 leading-relaxed font-inter">
                Fortune 500-level expertise at a predictable cost. Our fractional model delivers premium legal services while preserving capital for growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Firm Overview & ROI Data */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-8 font-playfair">Why VTC Legal Exists</h2>
              <div className="space-y-6 text-gray-700 font-inter">
                <p className="text-lg leading-relaxed">
                  VTC Legal was founded to solve a critical business problem: the gap between expensive Big Law firms that drain budgets and inexperienced legal resources that create risk.
                </p>
                
                <p className="leading-relaxed">
                  Technology companies need sophisticated legal counsel that understands both regulatory complexity and business velocity. Traditional legal models force companies to choose between speed and expertise—we provide both.
                </p>
                
                <p className="leading-relaxed">
                  Our fractional general counsel model delivers Fortune 500-level legal strategy at a predictable monthly investment, enabling companies to allocate more capital toward product development and market expansion.
                </p>
                
                <p className="leading-relaxed">
                  With extensive cross-border experience throughout Latin America, we help technology companies expand globally while maintaining regulatory compliance and operational efficiency.
                </p>
              </div>
            </div>
            
            <div>
              <div className="bg-gray-50 p-10 rounded-2xl border border-gray-200">
                <h3 className="text-3xl font-bold text-gray-900 mb-10 font-playfair text-center">Proven Business Impact</h3>
                
                <div className="space-y-8">
                  <div className="text-center pb-6 border-b border-gray-200">
                    <div className="text-5xl font-bold text-teal-600 font-inter mb-3">70%</div>
                    <div className="text-lg font-semibold text-gray-900 font-inter">Cost Reduction</div>
                    <div className="text-sm text-gray-600 mt-2 font-inter">vs. Full-Time General Counsel</div>
                    <div className="text-xs text-gray-500 mt-1 font-inter">Save $200K-400K annually</div>
                  </div>

                  <div className="grid grid-cols-2 gap-8">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-gray-900 font-inter mb-2">48hrs</div>
                      <div className="text-sm font-medium text-gray-700 font-inter">Average Response</div>
                      <div className="text-xs text-gray-500 mt-1 font-inter">Business days</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-gray-900 font-inter mb-2">95%</div>
                      <div className="text-sm font-medium text-gray-700 font-inter">Client Retention</div>
                      <div className="text-xs text-gray-500 mt-1 font-inter">Year-over-year</div>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-6">
                    <h4 className="font-semibold text-gray-900 mb-4 font-inter text-center">Performance Metrics</h4>
                    <div className="space-y-3 text-sm font-inter">
                      <div className="flex justify-between">
                        <span className="text-gray-700">Contract Review Turnaround</span>
                        <span className="font-semibold text-gray-900">2-3 business days</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Risk Reduction</span>
                        <span className="font-semibold text-gray-900">85% improvement</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Proactive Issue Prevention</span>
                        <span className="font-semibold text-gray-900">90% resolution rate</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Time to Legal Decision</span>
                        <span className="font-semibold text-gray-900">Same day guidance</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-24">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 font-playfair">Our Mission & Values</h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto font-inter">
              Aligning legal strategy with business objectives to create measurable value for technology companies
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-16">
            <div className="text-center">
              <div className="w-20 h-20 bg-gray-900 rounded-2xl flex items-center justify-center mx-auto mb-8">
                <i className="ri-lightbulb-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 font-playfair">Innovation-Focused</h3>
              <p className="text-gray-700 leading-relaxed font-inter">
                We understand technology businesses because we live and breathe innovation. Our legal strategies enable growth, not hinder it, while maintaining competitive advantages.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-gray-900 rounded-2xl flex items-center justify-center mx-auto mb-8">
                <i className="ri-team-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 font-playfair">Strategic Partnership</h3>
              <p className="text-gray-700 leading-relaxed font-inter">
                We integrate as members of your leadership team, aligning with business objectives and growth goals. Legal decisions become strategic business decisions.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-gray-900 rounded-2xl flex items-center justify-center mx-auto mb-8">
                <i className="ri-global-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 font-playfair">Global Perspective</h3>
              <p className="text-gray-700 leading-relaxed font-inter">
                Bilingual capabilities and extensive Latin American experience help clients expand internationally with confidence, compliance, and market understanding.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Practice Areas */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-24">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 font-playfair">Core Practice Areas</h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto font-inter">
              Comprehensive legal services tailored for technology companies at every stage of growth
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-gray-50 p-10 rounded-xl shadow-sm border border-gray-200">
              <div className="w-16 h-16 bg-gray-900 rounded-lg flex items-center justify-center mb-8">
                <i className="ri-cpu-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 font-playfair">AI Governance & Compliance</h3>
              <p className="text-gray-700 leading-relaxed font-inter mb-6">
                Navigate evolving AI regulations with comprehensive governance frameworks, risk assessment protocols, and compliance strategies tailored to your technology stack.
              </p>
              <a href="/ai-governance" className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer font-inter">Learn More →</a>
            </div>

            <div className="bg-gray-50 p-10 rounded-xl shadow-sm border border-gray-200">
              <div className="w-16 h-16 bg-gray-900 rounded-lg flex items-center justify-center mb-8">
                <i className="ri-handshake-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 font-playfair">Commercial Strategy</h3>
              <p className="text-gray-700 leading-relaxed font-inter mb-6">
                Structure and negotiate complex commercial agreements that protect your interests while enabling growth, from enterprise SaaS deals to strategic partnerships.
              </p>
              <a href="/commercial-strategy" className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer font-inter">Learn More →</a>
            </div>

            <div className="bg-gray-50 p-10 rounded-xl shadow-sm border border-gray-200">
              <div className="w-16 h-16 bg-gray-900 rounded-lg flex items-center justify-center mb-8">
                <i className="ri-building-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 font-playfair">Corporate Law</h3>
              <p className="text-gray-700 leading-relaxed font-inter mb-6">
                Build solid legal foundations with expert guidance on corporate governance, equity structures, fundraising support, and routine corporate matters.
              </p>
              <a href="/corporate-law" className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer font-inter">Learn More →</a>
            </div>

            <div className="bg-gray-50 p-10 rounded-xl shadow-sm border border-gray-200">
              <div className="w-16 h-16 bg-gray-900 rounded-lg flex items-center justify-center mb-8">
                <i className="ri-compass-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 font-playfair">Strategic Planning</h3>
              <p className="text-gray-700 leading-relaxed font-inter mb-6">
                Align legal strategy with business objectives through proactive planning, risk management, and strategic counsel that considers both protection and opportunity.
              </p>
              <a href="/strategic-planning" className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer font-inter">Learn More →</a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose VTC */}
      <section className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-24">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 font-playfair">Why Choose VTC Legal</h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto font-inter">
              Strategic advantages that deliver measurable business value
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div>
              <div className="space-y-12">
                <div className="flex items-start">
                  <div className="w-16 h-16 bg-teal-600 rounded-xl flex items-center justify-center mr-8 flex-shrink-0">
                    <i className="ri-trophy-line text-white text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">Business-Tested Expertise</h3>
                    <p className="text-gray-700 leading-relaxed font-inter">
                      Extensive in-house Legal Director experience at high-growth SaaS and AI companies, combined with Big Law training and sophisticated transaction experience.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-16 h-16 bg-teal-600 rounded-xl flex items-center justify-center mr-8 flex-shrink-0">
                    <i className="ri-speed-line text-white text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">Immediate Business Impact</h3>
                    <p className="text-gray-700 leading-relaxed font-inter">
                      Deploy experienced legal counsel within days, not months. Start addressing critical legal needs immediately while maintaining business momentum.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-16 h-16 bg-teal-600 rounded-xl flex items-center justify-center mr-8 flex-shrink-0">
                    <i className="ri-funds-line text-white text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">Capital-Efficient Excellence</h3>
                    <p className="text-gray-700 leading-relaxed font-inter">
                      Save $200K-400K annually compared to hiring a full-time General Counsel while preserving capital for product development and market expansion.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:pl-12">
              <div className="bg-white p-12 rounded-2xl shadow-lg border border-gray-200">
                <h3 className="text-3xl font-bold text-gray-900 mb-10 font-playfair text-center">Client Industries</h3>
                
                <div className="space-y-8">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center mr-6">
                      <i className="ri-robot-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 font-inter text-lg">Artificial Intelligence</h4>
                      <p className="text-gray-600 font-inter">ML platforms, AI governance, algorithmic compliance, data privacy</p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center mr-6">
                      <i className="ri-cloud-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 font-inter text-lg">SaaS & Cloud Services</h4>
                      <p className="text-gray-600 font-inter">Enterprise software, subscription models, API agreements</p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center mr-6">
                      <i className="ri-bank-card-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 font-inter text-lg">FinTech</h4>
                      <p className="text-gray-600 font-inter">Payment processing, financial services, regulatory compliance</p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center mr-6">
                      <i className="ri-seedling-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 font-inter text-lg">Venture-Backed Startups</h4>
                      <p className="text-gray-600 font-inter">Fundraising, corporate governance, growth strategy</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-32 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-8 font-playfair">
            Ready to Drive Business Growth?
          </h2>
          <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto font-inter">
            Let's discuss how strategic legal counsel can accelerate your company's growth and maximize enterprise value.
          </p>
          <a href="/#contact" className="bg-teal-600 text-white px-12 py-4 rounded-lg text-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer inline-block font-inter">
            Schedule Business Consultation
          </a>
        </div>
      </section>

      {/* Founder Section - Footer */}
      <footer className="bg-gray-50 text-gray-900 py-20 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Founder Section */}
          <div className="mb-20">
            <div className="text-center mb-16">
              <h3 className="text-3xl font-bold text-gray-900 mb-4 font-playfair">Founder</h3>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="text-center lg:text-left">
                <img 
                  src="https://static.readdy.ai/image/a19705f045df358eda503236ce656b36/380eb7c86727b131baa89cf92ebbf8dc.png" 
                  alt="Sergio A. Torres - Founder and Legal Director at VTC Legal" 
                  className="w-48 h-64 object-cover object-top rounded-2xl shadow-lg mx-auto lg:mx-0"
                />
              </div>
              
              <div>
                <h4 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">Sergio A. Torres</h4>
                <p className="text-lg text-gray-700 mb-6 font-inter">Founder & Legal Director</p>
                
                <div className="space-y-4 text-gray-700 font-inter">
                  <p className="leading-relaxed">
                    Licensed to practice in New York and California with over a decade of business-focused legal experience serving as Legal Director for high-growth SaaS and AI companies. Combines Big Law training with in-house technology experience.
                  </p>
                  
                  <p className="leading-relaxed">
                    Bilingual attorney fluent in Spanish with extensive experience structuring cross-border transactions throughout Latin America, including Mexico, Brazil, Colombia, and other LATAM markets.
                  </p>
                  
                  <p className="leading-relaxed">
                    Specializes in AI governance, commercial strategy, and corporate law for technology companies navigating complex regulatory environments while maintaining competitive growth trajectories.
                  </p>
                </div>
                
                <div className="mt-8 flex flex-col sm:flex-row gap-4">
                  <a href="/#contact" className="bg-black text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors text-center cursor-pointer font-inter">
                    Schedule Consultation
                  </a>
                  <a href="https://www.linkedin.com/in/sergio-torres-law/" target="_blank" rel="noopener noreferrer" className="border border-gray-300 text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-center cursor-pointer font-inter">
                    View LinkedIn Profile
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          {/* Footer Copyright */}
          <div className="border-t border-gray-300 pt-10 text-center">
            <p className="text-gray-600 text-sm font-inter">
              © 2026 VTC Legal. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
